/**
 * Operator wire protocol v1 (PROTOCOL.md at the workspace root) — the raw-input
 * contract with mmr-webrtc-backend. The UI sends the active input source and its raw
 * data; all mapping to robot motion happens on board (mmr-planner).
 *
 * Two transports are injected: `sendReliable` (DataChannel `chat`: source select, stop,
 * camera, planner goals) and `sendInput` (DataChannel `control`: the operator-input stream). This
 * module owns the session id (`sid`) and the input `seq`; it knows nothing about
 * devices, DOM or React.
 */

import { v4 } from "uuid";

export const PROTOCOL_VERSION = 1;

export type InputKind = "keyboard" | "gamepad" | "xr";
export type ControlMode = "off" | InputKind;

/** Identity of an input source as sent on the wire (PROTOCOL.md §3.4). */
export interface InputSourceRef {
  id: string;
  kind: InputKind;
  label?: string;
  mapping?: string;
  profiles?: string[];
}

export type StopReason =
  "operator" | "source-change" | "xr-session-end" | "blur" | "unload" | "gamepad-disconnected";

/** Pose: position (m) + orientation quaternion [x, y, z, w] (PROTOCOL.md §3.3). */
export interface WirePose {
  p: [number, number, number];
  q: [number, number, number, number];
}

export interface KeyboardData {
  keys: string[];
}

export interface GamepadData {
  mapping: string;
  axes: number[];
  buttons: number[];
  pressed: number[];
}

export interface XrHandData {
  handedness: "left" | "right" | "none";
  hand: boolean;
  profiles: string[];
  grip: WirePose | null;
  ray: WirePose | null;
  buttons: number[];
  pressed: number[];
  touched: number[];
  axes: number[];
}

export interface XrData {
  frame_t: number;
  ref: string;
  head: WirePose | null;
  hands: XrHandData[];
}

export type InputData = KeyboardData | GamepadData | XrData;

export interface OperatorProtocolOptions {
  /** Reliable, ordered channel (`chat`). Returns true when sent over an open channel
   * (false = queued by the transport until it opens). */
  sendReliable: (text: string) => boolean;
  /** Unreliable input channel (`control`). Returns false when the frame was dropped. */
  sendInput: (text: string) => boolean;
  /** Clock for `t` (ms, wall clock). Injected for tests. */
  now?: () => number;
  /** Session id generator. Injected for tests. */
  newSid?: () => string;
  /** Goal id generator. Injected for tests. */
  newGoalId?: () => string;
}

export interface OperatorProtocol {
  /** Operator session id — stable for this object's lifetime (one page mount). */
  readonly sid: string;
  /** seq of the last operator-input frame handed to the transport. */
  readonly seq: number;
  selectSource(source: InputSourceRef | null): boolean;
  /** Desktop operator's explicit choice; the Quest still supplies its own XR frames. */
  selectMode(mode: ControlMode): boolean;
  sendInput(source: InputSourceRef, data: InputData): boolean;
  stop(reason?: StopReason): boolean;
  /** Observer's decoded-video heartbeat; backend gates Panther motion on it. */
  monitorHeartbeat(live: boolean): boolean;
  camera(mount: string, enabled: boolean): boolean;
  /** Ask the planner to move to a named goal (PROTOCOL.md §3.8); returns the new goal id. */
  goal(name: string): string;
  /** Cancel a goal (this one, or whatever runs when omitted) — PROTOCOL.md §3.9. */
  cancelGoal(goalId?: string | null): boolean;
}

function envelope(event: string, payload: object): string {
  return JSON.stringify({ event, payload });
}

export function createOperatorProtocol({
  sendReliable,
  sendInput,
  now = () => performance.timeOrigin + performance.now(),
  newSid = v4,
  newGoalId = v4,
}: OperatorProtocolOptions): OperatorProtocol {
  const sid = newSid();
  let seq = 0;

  return {
    sid,
    get seq() {
      return seq;
    },

    selectSource(source) {
      return sendReliable(envelope("input-source-select", { sid, source }));
    },

    selectMode(mode) {
      return sendReliable(envelope("control-mode-select", { sid, mode }));
    },

    sendInput(source, data) {
      seq += 1;
      return sendInput(
        envelope("operator-input", {
          v: PROTOCOL_VERSION,
          sid,
          seq,
          t: Math.round(now() * 10) / 10,
          source: { id: source.id, kind: source.kind },
          data,
        })
      );
    },

    stop(reason = "operator") {
      return sendReliable(envelope("send-stop", { sid, reason }));
    },

    monitorHeartbeat(live) {
      return sendReliable(envelope("monitor-heartbeat", { live }));
    },

    camera(mount, enabled) {
      return sendReliable(envelope("camera-control", { mount, enabled }));
    },

    goal(name) {
      const goal_id = newGoalId();
      sendReliable(envelope("planner-goal", { sid, goal_id, type: "named", name }));
      return goal_id;
    },

    cancelGoal(goalId = null) {
      return sendReliable(envelope("planner-cancel", { sid, goal_id: goalId }));
    },
  };
}
